/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { View, Icon } from '@components/atoms'
import { mono, boFont } from '@components/systems/backoffice/backoffice.tokens'
import { IconButton, Divider, Select, MenuItem, Tooltip } from '@mui/material'

import { useEditor, EditorContent, Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import Underline from '@tiptap/extension-underline'
import TextAlign from '@tiptap/extension-text-align'
import TextStyle from '@tiptap/extension-text-style'
import { Color } from '@tiptap/extension-color'
import Highlight from '@tiptap/extension-highlight'
import Placeholder from '@tiptap/extension-placeholder'

interface PropsType {
	/** 처음 한 번만 넣는 HTML */
	initialHtml: string
	placeholder?: string
	/** 붙여넣기·드롭·툴바로 들어온 이미지를 올리고 URL 을 돌려준다. 실패하면 '' */
	onUploadImage: (file: File) => Promise<string>
	onChange: (html: string, isEmpty: boolean) => void
	disabled?: boolean
}

const HEADING_OPTIONS = [
	{ value: 'p', label: '본문' },
	{ value: '1', label: '제목 1' },
	{ value: '2', label: '제목 2' },
	{ value: '3', label: '제목 3' },
]

/**
 * 이메일 답변 본문 에디터 — TipTap(MIT).
 * 이미지는 base64 로 박지 않고 서버(S3)에 올린 URL 을 넣는다 — Gmail 이 data: 이미지를 막는다.
 */
export const InquiryReplyEditor = (props: PropsType) => {
	const { initialHtml, placeholder, onUploadImage, onChange, disabled } = props

	const imageInputRef = useRef<HTMLInputElement | null>(null)
	const colorInputRef = useRef<HTMLInputElement | null>(null)
	const [isUploading, setIsUploading] = useState(false)
	// 렌더 갱신용 — 툴바 활성 표시는 selectionUpdate 마다 다시 그려야 한다
	const [, setTick] = useState(0)

	const uploadRef = useRef(onUploadImage)
	uploadRef.current = onUploadImage

	const insertImages = useCallback(async (editorInstance: Editor, files: File[]) => {
		const images = files.filter(aFile => aFile.type.startsWith('image/'))
		if (images.length === 0) return false
		setIsUploading(true)
		try {
			for (const aFile of images) {
				const url = await uploadRef.current(aFile)
				if (url) editorInstance.chain().focus().setImage({ src: url }).run()
			}
		} finally {
			setIsUploading(false)
		}
		return true
	}, [])

	const editor = useEditor({
		extensions: [
			StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
			Underline,
			TextStyle,
			Color,
			Highlight.configure({ multicolor: false }),
			TextAlign.configure({ types: ['heading', 'paragraph'] }),
			Link.configure({ openOnClick: false, autolink: true, defaultProtocol: 'https' }),
			Image.configure({ inline: false, allowBase64: false }),
			Placeholder.configure({ placeholder: placeholder ?? '' }),
		],
		content: initialHtml,
		editable: !disabled,
		editorProps: {
			attributes: { class: 'replyEditorContent' },
			handlePaste: (_view, event) => {
				const files = Array.from(event.clipboardData?.files ?? [])
				if (files.some(aFile => aFile.type.startsWith('image/')) && editor) {
					event.preventDefault()
					void insertImages(editor, files)
					return true
				}
				return false
			},
			handleDrop: (_view, event, _slice, moved) => {
				const files = Array.from(event.dataTransfer?.files ?? [])
				if (!moved && files.some(aFile => aFile.type.startsWith('image/')) && editor) {
					event.preventDefault()
					void insertImages(editor, files)
					return true
				}
				return false
			},
		},
		onUpdate: ({ editor: e }) => {
			onChange(e.getHTML(), e.isEmpty)
		},
		onSelectionUpdate: () => setTick(t => t + 1),
		onTransaction: () => setTick(t => t + 1),
	})

	useEffect(() => {
		editor?.setEditable(!disabled)
	}, [editor, disabled])

	const onPickImage = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const files = Array.from(e.target.files ?? [])
			e.target.value = ''
			if (editor) void insertImages(editor, files)
		},
		[editor, insertImages],
	)

	const onSetLink = useCallback(() => {
		if (!editor) return
		const previous = editor.getAttributes('link').href as string | undefined
		const url = window.prompt('링크 주소', previous ?? 'https://')
		if (url === null) return
		if (url.trim() === '') {
			editor.chain().focus().extendMarkRange('link').unsetLink().run()
			return
		}
		editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run()
	}, [editor])

	if (!editor) return null

	const headingValue = editor.isActive('heading', { level: 1 })
		? '1'
		: editor.isActive('heading', { level: 2 })
		? '2'
		: editor.isActive('heading', { level: 3 })
		? '3'
		: 'p'

	const tool = (
		name: string,
		title: string,
		onClick: () => void,
		isActive = false,
		isDisabled = false,
	) => (
		<Tooltip title={title} key={title}>
			<span>
				<IconButton
					size="small"
					onClick={onClick}
					disabled={disabled || isDisabled}
					className={isActive ? 'toolActive' : ''}
				>
					<Icon name={name} style={{ fontSize: 18, color: 'inherit' }} />
				</IconButton>
			</span>
		</Tooltip>
	)

	return (
		<View css={style} className={`replyEditor ${disabled ? 'disabled' : ''}`}>
			<View className="toolbar">
				{tool(
					'undo',
					'실행 취소',
					() => editor.chain().focus().undo().run(),
					false,
					!editor.can().undo(),
				)}
				{tool(
					'redo',
					'다시 실행',
					() => editor.chain().focus().redo().run(),
					false,
					!editor.can().redo(),
				)}
				<Divider orientation="vertical" flexItem className="toolDivider" />

				<Select
					value={headingValue}
					size="small"
					variant="standard"
					disableUnderline
					disabled={disabled}
					className="headingSelect"
					onChange={e => {
						const v = e.target.value
						if (v === 'p') editor.chain().focus().setParagraph().run()
						else
							editor
								.chain()
								.focus()
								.toggleHeading({ level: Number(v) as 1 | 2 | 3 })
								.run()
					}}
				>
					{HEADING_OPTIONS.map(opt => (
						<MenuItem key={opt.value} value={opt.value}>
							{opt.label}
						</MenuItem>
					))}
				</Select>
				<Divider orientation="vertical" flexItem className="toolDivider" />

				{tool(
					'format-bold',
					'굵게',
					() => editor.chain().focus().toggleBold().run(),
					editor.isActive('bold'),
				)}
				{tool(
					'format-italic',
					'기울임',
					() => editor.chain().focus().toggleItalic().run(),
					editor.isActive('italic'),
				)}
				{tool(
					'format-underline',
					'밑줄',
					() => editor.chain().focus().toggleUnderline().run(),
					editor.isActive('underline'),
				)}
				{tool(
					'format-strikethrough',
					'취소선',
					() => editor.chain().focus().toggleStrike().run(),
					editor.isActive('strike'),
				)}
				{tool(
					'format-color-text',
					'글자 색',
					() => colorInputRef.current?.click(),
					!!editor.getAttributes('textStyle').color,
				)}
				<input
					ref={colorInputRef}
					type="color"
					className="hiddenInput"
					onChange={e => editor.chain().focus().setColor(e.target.value).run()}
				/>
				{tool(
					'marker',
					'형광펜',
					() => editor.chain().focus().toggleHighlight().run(),
					editor.isActive('highlight'),
				)}
				{tool('format-clear', '서식 지우기', () =>
					editor.chain().focus().unsetAllMarks().clearNodes().run(),
				)}
				<Divider orientation="vertical" flexItem className="toolDivider" />

				{tool(
					'format-align-left',
					'왼쪽 정렬',
					() => editor.chain().focus().setTextAlign('left').run(),
					editor.isActive({ textAlign: 'left' }),
				)}
				{tool(
					'format-align-center',
					'가운데 정렬',
					() => editor.chain().focus().setTextAlign('center').run(),
					editor.isActive({ textAlign: 'center' }),
				)}
				{tool(
					'format-align-right',
					'오른쪽 정렬',
					() => editor.chain().focus().setTextAlign('right').run(),
					editor.isActive({ textAlign: 'right' }),
				)}
				<Divider orientation="vertical" flexItem className="toolDivider" />

				{tool(
					'format-list-bulleted',
					'글머리 기호',
					() => editor.chain().focus().toggleBulletList().run(),
					editor.isActive('bulletList'),
				)}
				{tool(
					'format-list-numbered',
					'번호 목록',
					() => editor.chain().focus().toggleOrderedList().run(),
					editor.isActive('orderedList'),
				)}
				{tool(
					'format-quote-close',
					'인용',
					() => editor.chain().focus().toggleBlockquote().run(),
					editor.isActive('blockquote'),
				)}
				{tool(
					'code-tags',
					'코드 블록',
					() => editor.chain().focus().toggleCodeBlock().run(),
					editor.isActive('codeBlock'),
				)}
				{tool('minus', '구분선', () => editor.chain().focus().setHorizontalRule().run())}
				<Divider orientation="vertical" flexItem className="toolDivider" />

				{tool('link-variant', '링크', onSetLink, editor.isActive('link'))}
				{tool(
					'image-outline',
					isUploading ? '이미지 올리는 중…' : '이미지 넣기',
					() => imageInputRef.current?.click(),
					false,
					isUploading,
				)}
				<input
					ref={imageInputRef}
					type="file"
					accept="image/*"
					multiple
					className="hiddenInput"
					onChange={onPickImage}
				/>
			</View>

			<EditorContent editor={editor} className="editorArea" />
		</View>
	)
}

const style = css({
	border: `1px solid ${mono.lineInput}`,
	borderRadius: 0,
	background: mono.surface,
	display: 'flex',
	flexDirection: 'column',
	minHeight: 0,
	transition: 'border-color 200ms ease-out',

	'&:focus-within': {
		borderColor: mono.ink,
	},
	'&.disabled': { background: mono.bg },

	'.toolbar': {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: 2,
		padding: '4px 6px',
		borderBottom: `1px solid ${mono.line}`,
		'.MuiIconButton-root': { color: mono.dim, borderRadius: 2 },
		'.toolActive': { color: mono.ink, background: mono.line },
		'.toolDivider': { margin: '4px 4px', borderColor: mono.line },
	},
	'.headingSelect': {
		fontSize: 13,
		minWidth: '4.5rem',
		'.MuiSelect-select': { padding: '4px 24px 4px 8px' },
	},
	'.hiddenInput': { display: 'none' },

	'.editorArea': {
		flex: 1,
		minHeight: 0,
		overflow: 'auto',
	},
	'.replyEditorContent': {
		minHeight: '16rem',
		padding: '12px 16px',
		outline: 'none',
		fontFamily: boFont.ui,
		fontSize: 15,
		fontWeight: 300,
		lineHeight: 1.6,
		textAlign: 'left',
		color: mono.ink,
		'& > * + *': { marginTop: '0.5em' },
		p: { margin: 0 },
		'p.is-editor-empty:first-of-type::before': {
			content: 'attr(data-placeholder)',
			color: mono.faint,
			float: 'left',
			height: 0,
			pointerEvents: 'none',
		},
		'h1, h2, h3': { margin: '0.6em 0 0.3em', lineHeight: 1.3, fontWeight: 400 },
		h1: { fontSize: 24 },
		h2: { fontSize: 20 },
		h3: { fontSize: 17 },
		'ul, ol': { paddingLeft: '1.5rem', margin: 0 },
		blockquote: {
			margin: 0,
			padding: '0.25rem 1rem',
			borderLeft: `1px solid ${mono.lineSoft}`,
			color: mono.dim,
		},
		pre: {
			background: mono.bg,
			padding: '0.75rem',
			borderRadius: 0,
			fontSize: 13,
			overflow: 'auto',
			fontFamily: boFont.mono,
		},
		code: { fontFamily: boFont.mono, fontSize: 13 },
		mark: { background: mono.line },
		a: { color: mono.ink, textDecoration: 'underline' },
		img: {
			maxWidth: '100%',
			height: 'auto',
			display: 'block',
			borderRadius: 0,
			'&.ProseMirror-selectednode': {
				outline: `1px solid ${mono.ink}`,
				outlineOffset: 2,
			},
		},
		hr: { border: 0, borderTop: `1px solid ${mono.line}`, margin: '1rem 0' },
	},
})
